import { useMaterialScopeOptions } from '../../hooks/useMaterialScopeOptions';
import { useCallback, useState } from 'react';
import {
  useInventoryArticles,
  useCreateInventoryArticle,
  useUpdateInventoryArticle,
  useDeleteInventoryArticle,
} from '../../../hooks/useInventoryArticles';
import {
  ITEMS_PER_PAGE,
  INITIAL_INVENTORY_ARTICLE_STATE,
} from '../constants/inventoryArticlesConstants';
import { usePermissions } from '../../../hooks/usePermissions';
import { useMaterialArticleHistory } from './useMaterialArticleHistory';

export const useInventoryArticlesTable = () => {
  const scopeOptions = useMaterialScopeOptions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(INITIAL_INVENTORY_ARTICLE_STATE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [filters, setFilters] = useState({ status: 'all' });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const { permissions } = usePermissions();
  const showCostHistory = Boolean(permissions.canViewMaterialDashboard);
  const costHistory = useMaterialArticleHistory(
    selectedArticleId,
    detailDialogOpen && showCostHistory
  );

  const { data: articlesResponse, isLoading } = useInventoryArticles({
    page,
    per_page: rowsPerPage,
    search: searchTerm,
    ...filters,
  });
  const createMutation = useCreateInventoryArticle();
  const updateMutation = useUpdateInventoryArticle();
  const deleteMutation = useDeleteInventoryArticle();

  const data = Array.isArray(articlesResponse)
    ? articlesResponse
    : articlesResponse?.results || [];
  const totalCount = Array.isArray(articlesResponse)
    ? data.length
    : articlesResponse?.total || 0;
  const totalPages = Math.max(
    1,
    Array.isArray(articlesResponse)
      ? Math.ceil(totalCount / rowsPerPage)
      : articlesResponse?.pages || 1
  );

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleOpenDialog = () => {
    setSelectedArticle(INITIAL_INVENTORY_ARTICLE_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedArticle(INITIAL_INVENTORY_ARTICLE_STATE);
    setEditMode(false);
  };

  const handleEdit = (article) => {
    setSelectedArticle(article);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleViewDetail = (article) => {
    setSelectedArticleId(article.uuid);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedArticleId(null);
  };

  const handleCreate = async (payload) => {
    await createMutation.mutateAsync(payload);
  };

  const handleUpdate = async (uuid, payload) => {
    await updateMutation.mutateAsync({ id: uuid, data: payload });
  };

  const handleDeleteRequest = (article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;
    await deleteMutation.mutateAsync(articleToDelete.uuid);
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const mutationsLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    scopeOptions,
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    dialogOpen,
    editMode,
    selectedArticle,
    deleteDialogOpen,
    articleToDelete,
    detailDialogOpen,
    selectedArticleId,
    showCostHistory,
    costHistoryLayers: costHistory.layers,
    costHistoryPurchases: costHistory.purchases,
    mutationsLoading,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleOpenDialog,
    handleCloseDialog,
    handleEdit,
    handleViewDetail,
    handleCloseDetailDialog,
    handleCreate,
    handleUpdate,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
